import { useState } from 'react'
import Avatar from '@mui/material/Avatar'
import { createAvatar, type Style } from '@dicebear/core'
import * as initials from '@dicebear/initials'
import * as avataaars from '@dicebear/avataaars'
import * as bottts from '@dicebear/bottts'
import type { AvatarStyle } from '@jupi/shared'
import { apiUrl } from '../api/client'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DICEBEAR_STYLES: Record<AvatarStyle, Style<any>> = {
  initials,
  avataaars,
  bottts,
}

// Deterministic and generated locally in the browser, mirroring mik-ng's
// UserAvatar -- unlike a Gravatar-style placeholder this never sends the
// member's email to a third party just to render a fallback image. A member
// without an uploaded photo gets one of these three generated styles, picked
// on their profile page.
export function getAvatarDataUri(seed: string, style: AvatarStyle = 'initials'): string {
  return createAvatar(DICEBEAR_STYLES[style] ?? initials, { seed: seed.trim().toLowerCase() }).toDataUri()
}

export interface UserAvatarProps {
  email: string
  displayName?: string
  avatarStyle?: AvatarStyle
  // With both, the uploaded photo is shown (served by GET /api/avatars/:id);
  // avatarVersion is the upload time, so a new photo is a new URL. Without
  // them, or when the photo is missing or hidden, the generated avatar is.
  userId?: string
  avatarVersion?: number | null
  size?: number
  onClick?: (event: React.MouseEvent<HTMLElement>) => void
}

export function UserAvatar({
  email,
  displayName,
  avatarStyle,
  userId,
  avatarVersion,
  size = 40,
  onClick,
}: UserAvatarProps) {
  const photoUrl = userId && avatarVersion ? apiUrl(`/api/avatars/${userId}?v=${avatarVersion}`) : null
  // The specific URL that failed, not a boolean: a later upload is a new
  // URL and must get its own chance.
  const [failedUrl, setFailedUrl] = useState<string | null>(null)
  const src =
    photoUrl && photoUrl !== failedUrl
      ? photoUrl
      : getAvatarDataUri(email || displayName || 'jupi', avatarStyle)
  return (
    <Avatar
      src={src}
      alt={displayName ?? email}
      onError={() => photoUrl && setFailedUrl(photoUrl)}
      onClick={onClick}
      sx={{ width: size, height: size, cursor: onClick ? 'pointer' : 'default' }}
    />
  )
}
